<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import moment from 'moment-jalaali'
import { useLocalization } from '@/composables/useLocalization'

const { __ } = useLocalization()

const props = defineProps<{
  tailUrl: string
  pollMs?: number
}>()

const lines = ref<string[]>([])
const path = ref('')
const exists = ref(true)
const modifiedAt = ref<number | null>(null)
const loading = ref(true)
const errorMessage = ref('')
const autoScroll = ref(true)
const logEl = ref<HTMLElement | null>(null)

const statusTone = computed(() => {
  if (!exists.value) return 'missing'
  if (errorMessage.value) return 'error'
  const age = modifiedAt.value ? Date.now() / 1000 - modifiedAt.value : null
  if (age === null) return 'unknown'
  if (age < 120) return 'live'
  if (age < 600) return 'aging'
  return 'stale'
})

const statusLabel = computed(() => {
  if (!exists.value) return __('Log file not found')
  if (errorMessage.value) return errorMessage.value
  const age = modifiedAt.value ? Date.now() / 1000 - modifiedAt.value : null
  if (age !== null && age < 120) return __('● live')
  if (age !== null) {
    return __('last write :n min ago').replace(':n', String(Math.round(age / 60)))
  }
  return '—'
})

function formatTehranJalali(unixSeconds: number): string {
  return moment(unixSeconds * 1000)
    .utcOffset(210)
    .format('jYYYY/jMM/jDD HH:mm:ss')
}

const metaBits = computed(() => {
  const bits: string[] = []
  if (lines.value.length) bits.push(`${lines.value.length.toLocaleString('fa-IR')} lines`)
  if (modifiedAt.value) {
    bits.push(formatTehranJalali(modifiedAt.value))
  }
  return bits
})

/** Highlight cycle separators; rewrite legacy Gregorian stamps to Jalali. */
function displayLine(line: string): string {
  return line.replace(
    /\[(\d{4})-(\d{2})-(\d{2}) (\d{2}:\d{2}:\d{2})\]/g,
    (_m, y, mo, d, t) => {
      const m = moment.utc(`${y}-${mo}-${d} ${t}`, 'YYYY-MM-DD HH:mm:ss').utcOffset(210)
      return `[${m.format('jYYYY/jMM/jDD')} ${m.format('HH:mm:ss')}]`
    },
  )
}

function lineTone(line: string): string {
  if (line.includes('═') || line.includes('◆◆')) {
    return 'cycle-sep text-emerald-500 dark:text-emerald-300 font-semibold tracking-wide my-1'
  }
  if (line.includes('شروع دور capture')) {
    return 'text-sky-600 dark:text-sky-300'
  }
  if (line.includes('پایان دور:')) {
    return 'text-amber-600 dark:text-amber-300'
  }
  return ''
}

async function poll() {
  try {
    const res = await fetch(`${props.tailUrl}?lines=400`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
    if (!res.ok) {
      throw new Error(res.statusText || 'Request failed')
    }
    const data = await res.json()
    exists.value = Boolean(data.exists)
    path.value = data.path || ''
    modifiedAt.value = data.modified_at ?? null
    errorMessage.value = ''

    const atBottom = logEl.value
      ? logEl.value.scrollHeight - logEl.value.scrollTop - logEl.value.clientHeight < 48
      : true

    lines.value = Array.isArray(data.lines) ? data.lines : []

    if (autoScroll.value && atBottom && logEl.value) {
      requestAnimationFrame(() => {
        if (logEl.value) logEl.value.scrollTop = logEl.value.scrollHeight
      })
    }
  } catch (error: any) {
    errorMessage.value = error?.message || __('Failed to fetch the log')
  } finally {
    loading.value = false
  }
}

let poller: ReturnType<typeof setInterval> | undefined
onMounted(() => {
  poll()
  poller = setInterval(poll, props.pollMs || 3000)
})
onBeforeUnmount(() => clearInterval(poller))
</script>

<template>
  <div class="scraper-log-app min-w-0 space-y-4 md:space-y-5">
    <header class="panel-glass rounded-[1.75rem] p-4 sm:rounded-[2rem] sm:p-5 md:p-6">
      <div class="panel-glass-content flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div class="min-w-0 space-y-2">
          <span class="panel-glass-chip inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold">
            <span class="relative flex size-2 shrink-0 overflow-hidden rounded-full">
              <span
                class="absolute inline-flex size-full animate-ping rounded-full opacity-60"
                :class="{
                  'bg-emerald-400': statusTone === 'live',
                  'bg-amber-400': statusTone === 'aging' || statusTone === 'stale',
                  'bg-rose-400': statusTone === 'missing' || statusTone === 'error',
                  'bg-zinc-400': statusTone === 'unknown',
                }"
              />
              <span
                class="relative inline-flex size-2 rounded-full"
                :class="{
                  'bg-emerald-500': statusTone === 'live',
                  'bg-amber-500': statusTone === 'aging' || statusTone === 'stale',
                  'bg-rose-500': statusTone === 'missing' || statusTone === 'error',
                  'bg-zinc-400': statusTone === 'unknown',
                }"
              />
            </span>
            Collector
          </span>
          <div>
            <h1 class="text-lg font-semibold tracking-tight text-zinc-900 dark:text-white sm:text-xl">
              {{ __('Collector log') }}
            </h1>
            <p class="mt-1 text-xs text-zinc-500 dark:text-zinc-400 sm:text-sm">
              {{ statusLabel }}
            </p>
          </div>
        </div>

        <label
          class="panel-glass-btn inline-flex cursor-pointer items-center gap-2 rounded-full px-4 py-2.5 text-sm"
        >
          <input
            v-model="autoScroll"
            type="checkbox"
            class="size-3.5 rounded border-zinc-300 text-primary-500 focus:ring-primary-400/30 dark:border-zinc-600"
          >
          {{ __('Auto-scroll') }}
        </label>
      </div>
    </header>

    <div v-if="metaBits.length || path" class="flex flex-wrap gap-2">
      <span
        v-for="bit in metaBits"
        :key="bit"
        class="panel-glass-card rounded-full px-3 py-1 font-mono text-[11px] text-zinc-600 dark:text-zinc-300"
      >
        <span class="panel-glass-content">{{ bit }}</span>
      </span>
      <span
        v-if="path"
        class="panel-glass-card max-w-full truncate rounded-full px-3 py-1 font-mono text-[11px] text-zinc-500 dark:text-zinc-400"
        :title="path"
      >
        <span class="panel-glass-content">{{ path }}</span>
      </span>
    </div>

    <section class="panel-glass overflow-hidden rounded-[1.75rem] p-3 sm:rounded-[2rem] sm:p-4 md:p-5">
      <div class="panel-glass-content min-w-0">
        <div
          ref="logEl"
          dir="ltr"
          class="panel-glass-inset panel-scrollbar max-h-[min(70vh,42rem)] min-h-[20rem] overflow-y-auto overscroll-contain rounded-2xl p-4 font-mono text-[11px] leading-relaxed text-zinc-700 dark:text-zinc-200 sm:text-xs"
        >
          <p v-if="loading" class="text-zinc-400">{{ __('Loading…') }}</p>
          <p v-else-if="!exists" class="text-rose-500 dark:text-rose-300">
            {{ __('Log file not found') }}
          </p>
          <p v-else-if="!lines.length" class="text-zinc-400">—</p>
          <template v-else>
            <div
              v-for="(line, i) in lines"
              :key="i"
              class="whitespace-pre-wrap break-words"
              :class="lineTone(line)"
            >
              {{ displayLine(line) }}
            </div>
          </template>
        </div>
      </div>
    </section>
  </div>
</template>
